// MasterCasinoProfitPanel — one control sets the profit posture for ALL casino
// games at once (2026-08-06). Presets fan an approximate target_profit_pct out to
// every game; the 8-quarter engine supplies the highs/lows. 2 presets run a
// bounded promotional loss (players win). Backend: GET/PUT
// /admin/platform/casino_master_profit. Money path proven end-to-end.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import ExplainedSetting from '@baaligames/shared-ui/admin/components/ExplainedSetting';

type Tone = 'recommended' | 'safe' | 'neutral' | 'risky';
const PRESETS: { value: string; label: string; tagline: string; tone: Tone; consequence: string }[] = [
  { value: 'festival', label: "Players' Festival", tagline: '≈ −10%', tone: 'risky',
    consequence: '🎉 Players WIN big — the house deliberately runs about a 10% loss for the day. Pure goodwill + word-of-mouth mode; you are spending to pull players in. Great for a launch or festival.' },
  { value: 'crowd_pleaser', label: 'Crowd-Pleaser', tagline: '≈ −5%', tone: 'risky',
    consequence: '😀 Players win noticeably more than they lose (house about −5%). A gentle giveaway that keeps them coming back, at a small cost to you.' },
  { value: 'fair', label: 'Fair Play', tagline: '≈ +5%', tone: 'safe',
    consequence: '⚖️ A thin house edge (~5%). Players win plenty and the games feel fair; you earn a modest, steady profit.' },
  { value: 'balanced', label: 'Balanced', tagline: '≈ +15%', tone: 'recommended',
    consequence: '✅ Healthy house profit (~15%) while players still win regularly across the day. The sensible everyday setting.' },
  { value: 'profit_focus', label: 'Profit Focus', tagline: '≈ +25%', tone: 'neutral',
    consequence: '💰 Strong house profit (~25%). Players win less often but still hit wins sometimes. Lean into earnings.' },
  { value: 'max_house', label: 'Max House', tagline: '≈ +40%', tone: 'neutral',
    consequence: '🔥 Maximum house profit (~40%). Players rarely win — this can slow play and word-of-mouth, so use it sparingly.' },
];
const labelFor = (v: string) => PRESETS.find((p) => p.value === v)?.label ?? '—';

// Phase B (plan §4.7): the Earnings tab must read as ONE section with ONE
// "Now active:" line — "Fair Play · Spotlight: Normal". The hub lifts the
// adrenaline GET and passes the fragment in; this panel never fetches it.
export default function MasterCasinoProfitPanel({ extraActive }: { extraActive?: string } = {}) {
  const [current, setCurrent] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('balanced');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await adminApi.get<{ current: { preset?: string } | null }>('/admin/platform/casino_master_profit');
        if (alive) { const cur = d?.current?.preset ?? null; setCurrent(cur); if (cur) setSelected(cur); }
      } catch (e) {
        if (alive) setErr(e instanceof ApiError ? e.message : 'Could not load.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  async function apply() {
    const p = PRESETS.find((x) => x.value === selected);
    if (!p) return;
    const warn = (selected === 'festival' || selected === 'crowd_pleaser')
      ? '\n\nThis is a PLAYERS-WIN preset — the house will run at a deliberate LOSS.' : '';
    if (!window.confirm(`Apply "${p.label}" (${p.tagline}) to ALL casino games?${warn}\n\n${p.consequence}`)) return;
    setSaving(true); setMsg(''); setErr('');
    try {
      await adminApi.put('/admin/platform/casino_master_profit', { preset: selected });
      setCurrent(selected);
      setMsg(`Applied "${p.label}" to all 9 casino games.`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Apply failed.');
    } finally { setSaving(false); }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-arena-900 dark:text-white flex items-center gap-2">
          <Icon name="tune" size={20} /> Master casino profit
        </h1>
        <p className="text-[13px] text-arena-500 dark:text-neon-200/70">
          One setting for how much every casino game earns you. Pick a posture — the games hit it on
          average with real ups and downs, so players still win sometimes. Two presets run a deliberate
          loss so players win big (great for growth).
        </p>
        <p className="text-[12px] font-semibold text-arena-600 dark:text-neon-200/80">
          Now active: <span className="text-violet-600 dark:text-violet-300">{current ? labelFor(current) : 'Custom / not set via master'}</span>
          {extraActive && (
            <>
              <span className="text-arena-400 dark:text-neon-200/50"> · </span>
              <span className="text-violet-600 dark:text-violet-300">{extraActive}</span>
            </>
          )}
        </p>
      </header>

      {loading ? (
        <div className="text-[13px] text-arena-500 dark:text-neon-200/60">Loading…</div>
      ) : (
        <>
          <ExplainedSetting
            title="Profit posture for all casino games"
            help="Applies an approximate target to Plinko, Lucky 7, Thimbles, Ludo, Snake & Ladder, Rummy, Tic-Tac-Toe, Chess and Carrom at once. Each game gets a small wiggle so they are not identical."
            value={selected}
            onChange={(v) => setSelected(String(v))}
            formatValue={(v) => labelFor(String(v))}
            presets={PRESETS}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={apply}
              disabled={saving}
              className="px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-bold shadow-[0_2px_10px_rgba(139,92,246,0.35)] transition active:scale-95"
            >
              {saving ? 'Applying…' : 'Apply to all casino games'}
            </button>
            {msg && <span className="text-[13px] text-emerald-600 dark:text-emerald-400">{msg}</span>}
            {err && <span className="text-[13px] text-rose-600 dark:text-rose-400">{err}</span>}
          </div>
        </>
      )}
    </div>
  );
}
